import { formatCents, formatCount, tt } from '@agos/shared'

/**
 * `/profile` — what the four tabs actually know about the person reading them.
 *
 * **FB-175.** *Môj výkon* reports nought euros an hour to nine of the twenty-three staff, beside a real
 * count of hours worked. `ProfileService.stats` attributes orders as
 * `"driver".equals(role) ? "driver_id = :id" : "employee_id = :id"`, and in this data `orders.employee_id`
 * is **only ever a waiter's** — 2 813 orders across three waiters. Every `admin` (6 of 6), every `cook`
 * (1 of 1) and every `main_admin` (2 of 2) has no attributable order at all.
 *
 * So an administrator who worked 18h 52m over four shifts is shown *0 objednávok · 0,00 € · 0,00 €/h*, with
 * the euros-per-hour in primary colour as the headline. It reads as a performance measurement of somebody
 * who generated nothing. It is not a measurement: orders are not attributed to their role at all.
 *
 * **FB-176.** A shift that lasted 67 milliseconds counts as a completed shift. Six of the 487 complete
 * attendance rows are under a minute and four are under a **second** — a double-clicked check-in and
 * check-out — across three people. `days`, `completedShifts` and the average are all computed over them:
 * this account's July reads *4 zmeny, priemer 4h 43m* where the two real shifts averaged **9h 26m**. The
 * average is halved by two records that represent no work at all.
 *
 * **FB-177.** The language picker offers three languages where the server publishes four.
 * `GET /settings/basic-public` answers `["sk", "hu", "en", "it"]`; legacy seeds the picker with a
 * hard-coded `["sk", "en", "hu"]` and replaces it only if the fetch succeeds — behind a `.catch(() => {})`.
 * So Italian is missing whenever that request fails, and somebody whose `preferredLanguage` is `it` sees no
 * button selected and no explanation.
 *
 * **FB-178.** Six of the fifty most recent activity entries render their raw database key.
 * `ACTION_LABELS` covers nine actions; the log holds `settle_tab`, `append_to_tab`, `open_tab`,
 * `return_to_kitchen`, `status_change:on_the_way` and `confirm_status:delivered` besides. `ACTION_COLORS`
 * is shorter still — it has no entry for `export` or `approve`, which `ACTION_LABELS` does name.
 */

// ─── The profile itself ──────────────────────────────────────────────────────

export interface Profile {
  id: number
  name: string
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  position: string
  systemRole: string
  avatarUrl: string | null
  bio: string | null
  preferredLanguage: string
  emailVerified: boolean
  isExternal: boolean
  createdAt: string | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0
}

function euros(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
  }
  return 0
}

export function profileOf(payload: unknown): Profile | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  if (typeof row.id !== 'number') return null
  return {
    id: row.id,
    name: text(row.name) ?? '',
    firstName: text(row.firstName),
    lastName: text(row.lastName),
    email: text(row.email),
    phone: text(row.phone),
    position: text(row.position) ?? '',
    systemRole: text(row.systemRole) ?? '',
    avatarUrl: text(row.avatarUrl),
    bio: text(row.bio),
    preferredLanguage: text(row.preferredLanguage) ?? 'sk',
    emailVerified: row.emailVerified === true,
    isExternal: row.isExternal === true,
    createdAt: text(row.createdAt),
  }
}

export function roleLabel(role: string): string {
  if (role === 'main_admin') return tt('Hlavný administrátor')
  if (role === 'admin') return tt('Administrátor')
  if (role === 'cook') return tt('Kuchár')
  if (role === 'waiter') return tt('Čašník')
  if (role === 'driver') return tt('Šofér')
  return role
}

/** Two initials for the avatar placeholder. A one-word name gives one letter, not a blank circle. */
export function initialsOf(name: string): string {
  const letters = name
    .split(' ')
    .map((word) => word.trim()[0])
    .filter((letter): letter is string => letter !== undefined)
    .slice(0, 2)
    .join('')
  return letters === '' ? '?' : letters.toUpperCase()
}

export function displayName(form: ProfileForm, fallback: string): string {
  const composed = [form.firstName, form.lastName].filter((part) => part.trim() !== '').join(' ').trim()
  return composed === '' ? fallback : composed
}

// ─── The personal form ───────────────────────────────────────────────────────

export interface ProfileForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  bio: string
  language: string
}

export const BIO_LIMIT = 500

export function formOf(profile: Profile): ProfileForm {
  return {
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    email: profile.email ?? '',
    phone: profile.phone ?? '',
    bio: profile.bio ?? '',
    language: profile.preferredLanguage,
  }
}

export function isDirty(form: ProfileForm, profile: Profile): boolean {
  const original = formOf(profile)
  return (Object.keys(original) as (keyof ProfileForm)[]).some((key) => form[key] !== original[key])
}

export type ProfileProblem =
  | { kind: 'bad-email'; value: string }
  | { kind: 'bio-too-long'; length: number }
  | { kind: 'unknown-language'; value: string }

/**
 * What the form refuses to send.
 *
 * Legacy checks nothing at all — `type="email"` on an input that is never in a `<form>`, so the browser
 * never validates it, and `maxLength` on the textarea, which a paste can exceed in some browsers and which
 * says nothing about what the server accepts.
 */
export function problemsOf(form: ProfileForm, languages: readonly string[]): ProfileProblem[] {
  const problems: ProfileProblem[] = []
  const email = form.email.trim()
  // Deliberately loose: the server is the authority, and a staff address may be anything with an @ and a dot.
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    problems.push({ kind: 'bad-email', value: email })
  }
  if (form.bio.length > BIO_LIMIT) {
    problems.push({ kind: 'bio-too-long', length: form.bio.length })
  }
  if (languages.length > 0 && !languages.includes(form.language)) {
    problems.push({ kind: 'unknown-language', value: form.language })
  }
  return problems
}

export function canSave(form: ProfileForm, profile: Profile, languages: readonly string[]): boolean {
  return isDirty(form, profile) && problemsOf(form, languages).length === 0
}

/** Blank fields are sent as `null`, which is how the server clears them; an omitted key leaves them alone. */
export function profilePayload(form: ProfileForm): Record<string, string | null> {
  return {
    firstName: form.firstName.trim() || null,
    lastName: form.lastName.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    bio: form.bio.trim() || null,
    preferredLanguage: form.language,
  }
}

/** Changing a verified address un-verifies it. Legacy says so, and it is worth keeping. */
export function verificationWouldReset(form: ProfileForm, profile: Profile): boolean {
  return profile.emailVerified && form.email.trim() !== (profile.email ?? '')
}

// ─── FB-177: the languages the server publishes ──────────────────────────────

/** What legacy seeds the picker with, and keeps whenever the request fails. */
export const LEGACY_FALLBACK_LANGUAGES: readonly string[] = ['sk', 'en', 'hu']

export function languagesOf(payload: unknown): string[] {
  const row = (payload ?? {}) as Record<string, unknown>
  const list = row.autoTranslateLanguages
  if (!Array.isArray(list)) return []
  return list.filter((entry): entry is string => typeof entry === 'string' && entry !== '')
}

/**
 * The picker's options.
 *
 * Empty rather than a hard-coded three when the server has not answered: offering a list that may be wrong
 * is worse than saying the list is not loaded, because the wrong list silently drops a language somebody is
 * already using. The person's own language is always included, so a profile set to `it` shows `it` even if
 * an administrator later switches Italian off.
 */
export function pickerLanguages(published: readonly string[], current: string): string[] {
  if (published.length === 0) return current === '' ? [] : [current]
  return published.includes(current) ? [...published] : [...published, current]
}

export function languagesNote(published: readonly string[], current: string): string {
  if (published.length === 0) {
    return tt('Zoznam jazykov sa nepodarilo načítať, takže je tu len ten váš. Skúste obnoviť stránku.')
  }
  if (!published.includes(current)) {
    return `${tt('Váš jazyk')} (${current.toUpperCase()}) ${tt('už nie je medzi zapnutými - administrátor ho vypol v Nastavenia → AI preklad.')}`
  }
  return tt('Zoznam vychádza zo zapnutých jazykov v Nastavenia → AI preklad.')
}

// ─── The password form ───────────────────────────────────────────────────────

export const PASSWORD_MINIMUM = 6

/** Legacy's five checks, kept: length 6, length 10, an upper-case letter, a digit, a symbol. */
export function passwordStrength(password: string): number {
  let score = 0
  if (password.length >= PASSWORD_MINIMUM) score += 1
  if (password.length >= 10) score += 1
  if (/[A-Z]/.test(password)) score += 1
  if (/[0-9]/.test(password)) score += 1
  if (/[^A-Za-z0-9]/.test(password)) score += 1
  return score
}

export function strengthLabel(score: number): string {
  if (score <= 0) return tt('Veľmi slabé')
  if (score === 1) return tt('Slabé')
  if (score === 2) return tt('Priemerné')
  if (score === 3) return tt('Dobré')
  if (score === 4) return tt('Silné')
  return tt('Veľmi silné')
}

export interface PasswordForm {
  current: string
  next: string
  confirm: string
}

export type PasswordProblem = 'no-current' | 'too-short' | 'mismatch' | 'same-as-current'

export function passwordProblems(form: PasswordForm): PasswordProblem[] {
  const problems: PasswordProblem[] = []
  if (form.current === '') problems.push('no-current')
  if (form.next.length < PASSWORD_MINIMUM) problems.push('too-short')
  if (form.next !== '' && form.confirm !== '' && form.next !== form.confirm) problems.push('mismatch')
  // Legacy accepts this and the server stores it, so the "you have been logged out everywhere" that follows
  // is for a password that did not change.
  if (form.next !== '' && form.next === form.current) problems.push('same-as-current')
  return problems
}

export function passwordProblemMessage(problem: PasswordProblem): string {
  if (problem === 'no-current') return tt('Zadajte súčasné heslo.')
  if (problem === 'too-short') return tt('Nové heslo musí mať aspoň 6 znakov.')
  if (problem === 'mismatch') return tt('Heslá sa nezhodujú.')
  return tt('Nové heslo je rovnaké ako súčasné - zmena by vás odhlásila zbytočne.')
}

export function canChangePassword(form: PasswordForm): boolean {
  return passwordProblems(form).length === 0 && form.confirm !== ''
}

// ─── FB-175: the performance tab ─────────────────────────────────────────────

export interface Performance {
  from: string
  to: string
  role: string
  orderCount: number
  revenueCents: number
  averageOrderCents: number
  cashCents: number
  cardTerminalCents: number
  cardCourierCents: number
  cardOnlineCents: number
  workedMinutes: number
  shiftCount: number
  revenuePerHourCents: number
}

export function performanceOf(payload: unknown): Performance | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const payments = (row.paymentBreakdown ?? {}) as Record<string, unknown>
  const attendance = (row.attendance ?? {}) as Record<string, unknown>
  return {
    from: text(row.from) ?? '',
    to: text(row.to) ?? '',
    role: text(row.role) ?? '',
    orderCount: count(row.orderCount),
    revenueCents: euros(row.revenue),
    averageOrderCents: euros(row.avgOrder),
    cashCents: euros(payments.cash),
    cardTerminalCents: euros(payments.cardTerminal),
    cardCourierCents: euros(payments.cardCourier),
    cardOnlineCents: euros(payments.cardOnline),
    workedMinutes: count(attendance.workedMinutes),
    shiftCount: count(attendance.shiftCount),
    revenuePerHourCents: euros(attendance.revenuePerHour),
  }
}

/**
 * The roles an order is ever attributed to.
 *
 * `ProfileService.stats` joins on `driver_id` for a driver and `employee_id` for everybody else, and
 * `employee_id` is set only for waiters. So these two roles get a measurement and the rest get a
 * structural zero.
 *
 * **Re-checked 2026-08-05, because this list is the one thing here that could silently go wrong.**
 * Every other *nemerané* on this screen is a count testing itself; this one gates on a role, and a role
 * that started carrying attributed orders would read *nemerané* while having real figures. It has not:
 * grouped over the restored data, `employee_id` joins **only** `waiter` (3 people) and `driver_id`
 * **only** `driver` (10 people). No third role appears on either side, so the pair is still exhaustive.
 *
 * The order counts are deliberately not repeated here. This comment said *2 813 orders* and the table
 * now says 3 104 — the suite writes orders, so that figure moves on its own and pinning it only teaches
 * the next reader that the note is unreliable. What does not move is the shape: two roles, and a join
 * that reaches no others.
 */
export const ATTRIBUTED_ROLES: readonly string[] = ['waiter', 'driver']

export function ordersAreAttributed(role: string): boolean {
  return ATTRIBUTED_ROLES.includes(role)
}

/** The order label legacy varies by role, kept. */
export function orderCountLabel(role: string): string {
  return role === 'driver' ? tt('Doručené objednávky') : tt('Spracované objednávky')
}

/**
 * Whether the takings figures mean anything for this person.
 *
 * `null` when they cannot — an administrator's nought is not a slow month, it is a column that is never
 * written for their role. Same rule as FB-113 and FB-167: unknown must not render as a flattering — or
 * damning — number.
 */
export function revenuePerHourCents(performance: Performance): number | null {
  if (!ordersAreAttributed(performance.role)) return null
  if (performance.workedMinutes <= 0) return null
  return performance.revenuePerHourCents
}

export function attributionNote(performance: Performance): string {
  if (ordersAreAttributed(performance.role)) return ''
  return `${tt('Objednávky sa k vašej roli')} (${roleLabel(performance.role)}) ${tt('nepriraďujú - objednávku nesie čašník, ktorý ju vzal, alebo šofér, ktorý ju doviezol. Nuly nižšie preto neznamenajú, že ste nič nespravili.')}`
}

/** The four payment rows, with the share of a total that may be nothing. */
export function performancePayments(performance: Performance): {
  key: string
  label: string
  cents: number
  percent: number | null
}[] {
  const rows = [
    { key: 'cash', label: tt('Hotovosť'), cents: performance.cashCents },
    { key: 'terminal', label: tt('Terminál'), cents: performance.cardTerminalCents },
    { key: 'courier', label: tt('Kuriérska karta'), cents: performance.cardCourierCents },
    { key: 'online', label: tt('CardPay (online)'), cents: performance.cardOnlineCents },
  ]
  return rows.map((row) => ({
    ...row,
    percent: performance.revenueCents > 0 ? (row.cents / performance.revenueCents) * 100 : null,
  }))
}

export function revenuePerShiftCents(performance: Performance): number | null {
  if (!ordersAreAttributed(performance.role) || performance.shiftCount <= 0) return null
  return Math.round(performance.revenueCents / performance.shiftCount)
}

// ─── FB-176: the attendance tab ──────────────────────────────────────────────

export interface AttendanceRecord {
  id: number
  workDate: string
  checkIn: string | null
  checkOut: string | null
  notes: string | null
  type: string | null
}

export interface AttendanceSummary {
  totalMinutes: number
  days: number
  completedShifts: number
}

export function recordsOf(payload: unknown): AttendanceRecord[] {
  const rows = (payload as { records?: unknown } | undefined)?.records
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const workDate = text(row.workDate)
    if (workDate === null) return []
    return [
      {
        id: count(row.id),
        workDate,
        checkIn: text(row.checkIn),
        checkOut: text(row.checkOut),
        notes: text(row.notes),
        type: text(row.type),
      },
    ]
  })
}

export function summaryOf(payload: unknown): AttendanceSummary | null {
  const row = (payload as { summary?: unknown } | undefined)?.summary
  if (row === null || row === undefined || typeof row !== 'object') return null
  const summary = row as Record<string, unknown>
  return {
    totalMinutes: count(summary.totalMinutes),
    days: count(summary.days),
    completedShifts: count(summary.completedShifts),
  }
}

/** Whole minutes between two stamps. A check-out before its check-in is not a negative shift; it is none. */
export function shiftMinutes(record: AttendanceRecord): number {
  if (record.checkIn === null || record.checkOut === null) return 0
  const start = Date.parse(record.checkIn)
  const end = Date.parse(record.checkOut)
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0
  return Math.floor((end - start) / 60_000)
}

export type ShiftState = 'open' | 'instant' | 'backwards' | 'worked' | 'none'

/**
 * What a record actually is.
 *
 * `instant` is the one legacy has no name for: a check-in and a check-out within the same minute — four of
 * them are within the same **second** — which the summary counts as a completed shift and averages over.
 */
export function shiftState(record: AttendanceRecord): ShiftState {
  if (record.checkIn === null) return 'none'
  if (record.checkOut === null) return 'open'
  const start = Date.parse(record.checkIn)
  const end = Date.parse(record.checkOut)
  if (Number.isNaN(start) || Number.isNaN(end)) return 'none'
  if (end < start) return 'backwards'
  return end - start < 60_000 ? 'instant' : 'worked'
}

export function realShifts(records: readonly AttendanceRecord[]): AttendanceRecord[] {
  return records.filter((record) => shiftState(record) === 'worked')
}

export function instantShifts(records: readonly AttendanceRecord[]): AttendanceRecord[] {
  return records.filter((record) => shiftState(record) === 'instant')
}

/**
 * The average shift, over shifts that happened.
 *
 * Legacy divides the summary's minutes by the summary's shift count, and the count includes the instant
 * ones — so a month with two real shifts and two double-clicks reports half the real average.
 */
export function averageShiftMinutes(records: readonly AttendanceRecord[]): number | null {
  const real = realShifts(records)
  if (real.length === 0) return null
  return Math.round(real.reduce((total, record) => total + shiftMinutes(record), 0) / real.length)
}

/** What legacy shows, kept so the two can be compared rather than described. */
export function legacyAverageShiftMinutes(summary: AttendanceSummary | null): number | null {
  if (summary === null || summary.completedShifts <= 0) return null
  return Math.floor(summary.totalMinutes / summary.completedShifts)
}

export function attendanceNote(records: readonly AttendanceRecord[]): string {
  const instant = instantShifts(records)
  if (instant.length === 0) return ''
  return `${formatCount(instant.length)} ${tt('zo záznamov trvá menej než minútu - príchod a odchod zapísané naraz. Do priemeru na zmenu sa nerátajú, do súčtu áno.')}`
}

export function formatMinutes(minutes: number): string {
  if (minutes <= 0) return '0h 00m'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return `${hours}h ${String(rest).padStart(2, '0')}m`
}

// ─── FB-178: the activity feed ───────────────────────────────────────────────

export interface ActivityEntry {
  id: number
  action: string
  entityType: string
  entityId: string | null
  createdAt: string
}

export function activityOf(payload: unknown): ActivityEntry[] {
  const rows = (payload as { entries?: unknown } | undefined)?.entries
  if (!Array.isArray(rows)) return []
  return rows.map((entry) => {
    const row = entry as Record<string, unknown>
    return {
      id: count(row.id),
      action: text(row.action) ?? '',
      entityType: text(row.entityType) ?? '',
      // The log stores it as a number or a string; anything else is not an id worth printing.
      entityId:
        typeof row.entityId === 'number' || typeof row.entityId === 'string'
          ? String(row.entityId)
          : null,
      createdAt: text(row.createdAt) ?? '',
    }
  })
}

/**
 * What an action is called.
 *
 * Legacy's nine, plus the six the log actually holds that it has never named. A `status_change:on_the_way`
 * is a compound key — the prefix is the action and the suffix is the status — so it is split rather than
 * added to the list one status at a time.
 */
export function actionLabel(action: string): string {
  const [head, tail] = action.split(':')
  if (tail !== undefined && head !== undefined) {
    if (head === 'status_change') return `${tt('Zmena stavu')} → ${tail}`
    if (head === 'confirm_status') return `${tt('Potvrdený stav')} → ${tail}`
    return `${actionLabel(head)} → ${tail}`
  }
  if (action === 'create') return tt('Vytvorené')
  if (action === 'update') return tt('Upravené')
  if (action === 'delete') return tt('Zmazané')
  if (action === 'login') return tt('Prihlásenie')
  if (action === 'logout') return tt('Odhlásenie')
  if (action === 'export') return tt('Export')
  if (action === 'approve') return tt('Schválené')
  if (action === 'storno') return tt('Storno')
  if (action === 'print') return tt('Tlač')
  if (action === 'open_tab') return tt('Otvorený účet')
  if (action === 'append_to_tab') return tt('Pridané na účet')
  if (action === 'settle_tab') return tt('Účet uzavretý')
  if (action === 'return_to_kitchen') return tt('Vrátené do kuchyne')
  // A key this table has not learned shows itself, rather than nothing.
  return action
}

/**
 * Whether the label came from the table or is the raw key showing through.
 *
 * Asked of the **whole** action, not of the part before the colon: `status_change` on its own has no entry
 * — only the compound form does — so splitting first reported every `status_change:…` as unknown, which is
 * exactly the class of entry this function exists to count.
 */
export function isNamedAction(action: string): boolean {
  return actionLabel(action) !== action
}

export function unnamedActions(entries: readonly ActivityEntry[]): string[] {
  return [...new Set(entries.filter((entry) => !isNamedAction(entry.action)).map((entry) => entry.action))]
}

export function actionColor(action: string): string {
  const head = action.split(':')[0] ?? action
  if (head === 'create' || head === 'open_tab') return 'emerald'
  if (head === 'update' || head === 'append_to_tab') return 'blue'
  if (head === 'delete') return 'red'
  if (head === 'login' || head === 'logout') return 'violet'
  if (head === 'storno' || head === 'return_to_kitchen') return 'orange'
  if (head === 'print' || head === 'export' || head === 'approve') return 'amber'
  if (head === 'settle_tab' || head === 'status_change' || head === 'confirm_status') return 'cyan'
  return 'zinc'
}

/** `pred 12 min`, `pred 3 h`, `pred 2 dňami`, then the date. Every branch through the translator. */
export function relativeTime(iso: string, now: number): string {
  const stamp = Date.parse(iso)
  if (Number.isNaN(stamp)) return '-'
  const elapsed = now - stamp
  if (elapsed < 60_000) return tt('pred chvíľou')
  const minutes = Math.floor(elapsed / 60_000)
  if (minutes < 60) return `${tt('pred')} ${formatCount(minutes)} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${tt('pred')} ${formatCount(hours)} h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${tt('pred')} ${formatCount(days)} ${days === 1 ? tt('dňom') : tt('dňami')}`
  return ''
}

// ─── The range picker both data tabs share ───────────────────────────────────

export interface Range {
  from: string
  to: string
}

export function moneyLabel(cents: number | null): string {
  return cents === null ? tt('nemerané') : formatCents(cents)
}
